import { css } from '@emotion/react'
import React from 'react'
import Arugula from '../assets/arugula.svg'
import Asparagus from '../assets/asparagus.svg'
import Beef from '../assets/beef.svg'
import BokChoy from '../assets/bokchoy.svg'
import Broccoli from '../assets/broccoli.svg'
import CannedFood from '../assets/cannedfood.svg'
import Carrots from '../assets/carrots.svg'
import Cauliflower from '../assets/cauliflower.svg'
import Cheese from '../assets/cheese.svg'
import Chicken from '../assets/chicken.svg'
import CottageCheese from '../assets/cottagecheese.svg'
import Cucumber from '../assets/cucumber.svg'
import EggWhites from '../assets/eggs.svg'
import ErythritolImage from '../assets/erythritol.svg'
import Flour from '../assets/flour.svg'
import Ham from '../assets/ham.svg'
import Lettuce from '../assets/lettuce.svg'
import MSGImage from '../assets/msg.svg'
import Pepper from '../assets/pepper.svg'
import PopcornSeasoningImage from '../assets/popcornseasoning.svg'
import Pork from '../assets/pork.svg'
import Mushroom from '../assets/portabellamushrooms.svg'
import Prawns from '../assets/prawns.svg'
import ProteinPowder from '../assets/proteinpowder.svg'
import Proteins from '../assets/proteins.svg'
import Salmon from '../assets/salmon.svg'
import Salt from '../assets/salt.svg'
import SaltAlt from '../assets/saltalt.svg'
import Scallop from '../assets/scallops.svg'
import Shellfish from '../assets/shellfish.svg'
import Tofu from '../assets/soy.svg'
import Spinach from '../assets/spinach.svg'
import Star from '../assets/star.svg'
import SteviaImage from '../assets/stevia.svg'
import SucraloseImage from '../assets/sucralose.svg'
import Squash from '../assets/summersquash.svg'
import Tilapia from '../assets/tilapia.svg'
import Tomato from '../assets/tomatoes.svg'
import Tuna from '../assets/tuna.svg'
import Turkey from '../assets/turkey.svg'
import Vegetables from '../assets/vegetables.svg'
import Yogurt from '../assets/yogurt.svg'
import Zucchini from '../assets/zucchini.svg'
import {
  Casein,
  Erythritol,
  MSG,
  Pea,
  PopcornSeasoning,
  Rice,
  Soy,
  Stevia,
  Sucralose,
  TVP,
  Whey,
} from '../components/affiliates/Groceries'
import { Footnote } from '../components/curriculum/Footnote'
import { FootnoteList } from '../components/curriculum/FootnoteList'
import { Explanation } from '../components/explanation/Explanation'
import { Image } from '../components/image/Image'
import { Layout } from '../components/Layout'
import { Tabs } from '../components/tabs/Tabs'
import { colors, md } from '../theme/theme'

type Group = 'Vegetables' | 'Protein' | 'Superstars' | 'Toppings'

type ProteinLeanness = 'Lean' | 'Semi-lean'

type ProteinType = 'Meat/fish' | 'Vegetarian' | 'Vegan'

const Groceries = () => {
  const [group, setGroup] = React.useState('Vegetables' as Group)
  const [proteinType, setProteinType] = React.useState(
    'Meat/fish' as ProteinType
  )
  const [proteinLeanness, setProteinLeanness] = React.useState(
    'Lean' as ProteinLeanness
  )

  const additiveFootnotes = [
    'http://physiqonomics.com/aspartame',
    'https://mennohenselmans.com/artificial-sweeteners-bitter-controversy/',
    'https://pubmed.ncbi.nlm.nih.gov/25297880',
    'https://pubmed.ncbi.nlm.nih.gov/25767289',
    'https://www.cochrane.org/CD009217/VASC_reduced-dietary-salt-prevention-cardiovascular-disease',
    'https://www.healthline.com/nutrition/msg-good-or-bad',
    'https://www.ncbi.nlm.nih.gov/pubmed/28231094',
    'https://www.scientificamerican.com/article/its-time-to-end-the-war-on-salt',
  ]

  const items = css`
    margin-top: 30px;
    > div {
      align-items: center;
      background-color: ${colors.white};
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin: 0 10px;
      min-width: 30%;
      padding: 20px 0;

      @media (max-width: ${md}px) {
        margin: 0 auto;
        min-width: 40%;
        padding: 0 10px;
      }

      div {
        margin-top: 10px;
        text-align: center;
      }

      img {
        margin: 0;

        @media (max-width: ${md}px) {
          margin-top: 20px;
        }
      }
    }
  `

  return (
    <Layout>
      <div className={'w90 center module'}>
        <h1 className="huge mb30">Groceries</h1>
        <Explanation color="blue">
          <div>
            <strong>Why: </strong>Learn what food to buy to make your fat loss
            easier.
          </div>
        </Explanation>

        <Tabs
          className="mt20"
          tabs={{
            Protein: {
              image: Proteins,
              title: 'Protein',
            },
            Superstars: {
              image: Star,
              title: 'Superstars',
            },
            Toppings: {
              image: Salt,
              title: 'Toppings',
            },
            Vegetables: {
              image: Vegetables,
              title: 'Vegetables',
            },
          }}
          css={css`
            button {
              height: 80px;
              margin: 10px 5px;
              img {
                height: 40px;
                margin: 0;
                margin-left: 10px;
                width: 40px;
              }
            }
          `}
          selected={group}
          onSelect={(tab: string | null) => {
            setGroup(tab as Group)
          }}
        />
        <div>
          {group === 'Vegetables' && (
            <div>
              <div className="fcs mt20">
                <Image
                  width={100}
                  height={100}
                  alt="Carrots and apples"
                  src={Vegetables}
                />
                <h2 className="mt20 mb20">Vegetables</h2>
              </div>
              <Explanation color="blue">
                <div>
                  <strong>Why:</strong> Vegetables, which are low in caloric
                  density and high in fiber, are great for keeping you full. You
                  can see a full list of foods, ranked by caloric density{' '}
                  <a
                    target="_blank"
                    href="https://app.cleanslate.sh/foods"
                    rel="noopener noreferer noreferrer"
                  >
                    here
                  </a>
                  . Anything <strong>below 40</strong> is a great option!
                </div>
              </Explanation>

              <div css={items} className={'fr wrap'}>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="asparagus"
                    src={Asparagus}
                  />
                  <div>Asparagus</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="broccoli" src={Broccoli} />
                  <div>Broccoli</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="carrots" src={Carrots} />
                  <div>Carrots</div>
                </div>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="cauliflower"
                    src={Cauliflower}
                  />
                  <div>Cauliflower</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="cucumber" src={Cucumber} />
                  <div>Cucumber</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="lettuce" src={Lettuce} />
                  <div>Lettuce</div>
                </div>

                <div>
                  <Image width={90} height={90} alt="mushroom" src={Mushroom} />
                  <div>Mushroom</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="pepper" src={Pepper} />
                  <div>Peppers</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="spinach" src={Spinach} />
                  <div>Spinach</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="squash" src={Squash} />
                  <div>Squash</div>
                </div>

                <div>
                  <Image width={90} height={90} alt="tomato" src={Tomato} />
                  <div>Tomatoes</div>
                </div>

                <div>
                  <Image width={90} height={90} alt="zucchini" src={Zucchini} />
                  <div>Zucchini</div>
                </div>
              </div>
            </div>
          )}

          {group === 'Protein' && (
            <div>
              <div className="fcs mt20">
                <Image
                  width={100}
                  height={100}
                  alt="strong arm flexing"
                  src={Proteins}
                />
                <h2 className="mt20 mb20">Protein</h2>
              </div>
              <Explanation color="blue">
                <div>
                  <strong>Why:</strong> Protein is crucial for fighting hunger
                  and keeping a diet safe. You can see a full list of foods,
                  ranked by protein density{' '}
                  <a
                    target="_blank"
                    href="https://app.cleanslate.sh/foods"
                    rel="noopener noreferer noreferrer"
                  >
                    here
                  </a>
                  . Anything <strong>above 70</strong> is a great option!
                </div>
              </Explanation>

              <Tabs
                className="mt20"
                tabs={{
                  Lean: {
                    image: '',
                    title: 'Lean',
                  },
                  'Semi-lean': {
                    image: '',
                    title: 'Semi-lean',
                  },
                }}
                selected={proteinLeanness}
                onSelect={(tab: string | null) => {
                  setProteinLeanness(tab as ProteinLeanness)
                }}
              />
              <Tabs
                className="mt20"
                tabs={{
                  'Meat/fish': {
                    image: '',
                    title: 'Meat/fish',
                  },
                  Vegan: {
                    image: '',
                    title: 'Vegan',
                  },
                  Vegetarian: {
                    image: '',
                    title: 'Vegetarian',
                  },
                }}
                selected={proteinType}
                onSelect={(tab: string | null) => {
                  setProteinType(tab as ProteinType)
                }}
              />

              {/* Items */}
              {proteinType === 'Meat/fish' && proteinLeanness === 'Lean' && (
                <div css={items} className={'fr wrap'}>
                  <div>
                    <Image width={90} height={90} alt="chicken" src={Chicken} />
                    <div>Chicken breast (skinless)</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="turkey" src={Turkey} />
                    <div>Turkey breast (skinless)</div>
                  </div>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="shrimp"
                      src={Shellfish}
                    />
                    <div>Shrimp</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="scallop" src={Scallop} />
                    <div>Scallop</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="prawn" src={Prawns} />
                    <div>Prawn</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="fish" src={Tuna} />
                    <div>Tuna</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="fish" src={Tilapia} />
                    <div>Tilapia</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="fish" src={CannedFood} />
                    <div className="ml5">Tuna or salmon (canned)</div>
                  </div>
                </div>
              )}

              {proteinType === 'Meat/fish' &&
                proteinLeanness === 'Semi-lean' && (
                  <div css={items} className={'fr wrap'}>
                    <div>
                      <Image width={90} height={90} alt="Beef" src={Beef} />
                      <div>Beef round</div>
                    </div>
                    <div>
                      <Image width={90} height={90} alt="Pork" src={Pork} />
                      <div>Pork chops</div>
                    </div>
                    <div>
                      <Image width={90} height={90} alt="Ham" src={Ham} />
                      <div>Ham</div>
                    </div>
                    <div>
                      <Image width={90} height={90} alt="Salmon" src={Salmon} />
                      <div>Salmon</div>
                    </div>
                  </div>
                )}

              {proteinType === 'Vegetarian' && proteinLeanness === 'Lean' && (
                <div css={items} className={'fr wrap'}>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="cottage cheese"
                      src={CottageCheese}
                    />
                    <div>Cottage cheese: fat-free</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="yogurt" src={Yogurt} />
                    <div>Greek yogurt: non-fat</div>
                  </div>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="egg whites"
                      src={EggWhites}
                    />
                    <div>Egg whites</div>
                  </div>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="protein powder"
                      src={ProteinPowder}
                    />
                    <div>
                      Casein | <Casein text="Buy" />
                    </div>
                  </div>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="protein powder"
                      src={ProteinPowder}
                    />
                    <div>
                      Whey | <Whey text="Buy" />
                    </div>
                  </div>
                </div>
              )}

              {proteinType === 'Vegetarian' &&
                proteinLeanness === 'Semi-lean' && (
                  <div css={items} className={'fr wrap'}>
                    <div>
                      <Image
                        width={90}
                        height={90}
                        alt="American cheese"
                        src={Cheese}
                      />
                      <div>American cheese</div>
                    </div>
                    <div>
                      <Image
                        width={90}
                        height={90}
                        alt="cottage cheese"
                        src={CottageCheese}
                      />
                      <div>Cottage cheese: 1%</div>
                    </div>
                    <div>
                      <Image width={90} height={90} alt="yogurt" src={Yogurt} />
                      <div>Greek yogurt: 2%</div>
                    </div>
                  </div>
                )}

              {proteinType === 'Vegan' && proteinLeanness === 'Lean' && (
                <div css={items} className={'fr wrap'}>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="protein powder"
                      src={ProteinPowder}
                    />
                    <div>
                      Pea powder | <Pea text="Buy" />
                    </div>
                  </div>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="protein powder"
                      src={ProteinPowder}
                    />
                    <div>
                      Rice powder | <Rice text="Buy" />
                    </div>
                  </div>
                  <div>
                    <Image
                      width={90}
                      height={90}
                      alt="protein powder"
                      src={ProteinPowder}
                    />
                    <div>
                      Soy powder | <Soy text="Buy" />
                    </div>
                  </div>
                </div>
              )}

              {proteinType === 'Vegan' && proteinLeanness === 'Semi-lean' && (
                <div css={items} className={'fr wrap'}>
                  <div>
                    <Image width={90} height={90} alt="Tofu" src={Tofu} />
                    <div>Tofu</div>
                  </div>
                  <div>
                    <Image width={90} height={90} alt="TVP" src={Flour} />
                    <div>
                      TVP | <TVP text="Buy" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {group === 'Superstars' && (
            <div>
              <div className="fcs mt20">
                <Image width={100} height={100} alt="Star" src={Star} />
                <h2 className="mt20 mb20">
                  Superstars: Protein <u>and</u> volume
                </h2>
              </div>
              <Explanation color="blue">
                <div>
                  <strong>Why:</strong> These foods combine a low caloric
                  density, a high protein density, and a high nutrient density.
                  Try to incorporate them in every meal!
                </div>
              </Explanation>

              <div css={items} className={'fr wrap'}>
                <div>
                  <Image width={90} height={90} alt="Arugula" src={Arugula} />
                  <div>Arugula</div>
                </div>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="Asparagus"
                    src={Asparagus}
                  />
                  <div>Asparagus</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="Bok Choy" src={BokChoy} />
                  <div>Bok Choy</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="broccoli" src={Broccoli} />
                  <div>Broccoli</div>
                </div>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="cauliflower"
                    src={Cauliflower}
                  />
                  <div>Cauliflower</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="Lettuce" src={Lettuce} />
                  <div>Lettuce</div>
                </div>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="Mushrooms"
                    src={Mushroom}
                  />
                  <div>Mushrooms</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="Spinach" src={Spinach} />
                  <div>Spinach</div>
                </div>
              </div>
            </div>
          )}

          {group === 'Toppings' && (
            <div>
              <div className="fcs mt20">
                <Image width={100} height={100} alt="salt" src={Salt} />
                <h2 className="mt20 mb20">Toppings</h2>
              </div>
              <Explanation color="blue">
                <div>
                  <strong>Why:</strong> These toppings can make vegetables and
                  lean protein taste better with few calories.
                </div>
              </Explanation>
              <Explanation color="yellow">
                <div>
                  <strong>Myths debunked:</strong> Researchers have
                  overwhelmingly shown that MSG, salt, and fake sugars, such as
                  aspartame or stevia, are safe and will not cause weight gain,
                  heart disease, or cancer{' '}
                  <FootnoteList links={additiveFootnotes} />. In fact, fake
                  sugars can cause <strong>weight loss</strong> when substituted
                  for soda{' '}
                  <Footnote link="https://www.ncbi.nlm.nih.gov/pubmed/26365102" />
                  .
                </div>
              </Explanation>

              <div css={items} className={'fr wrap'}>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="tofu"
                    src={ErythritolImage}
                  />
                  <div>
                    Erythritol | <Erythritol text="Buy" />
                  </div>
                </div>
                <div>
                  <Image width={90} height={90} alt="tofu" src={MSGImage} />
                  <div>
                    MSG | <MSG text="Buy" />
                  </div>
                </div>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="tofu"
                    src={PopcornSeasoningImage}
                  />
                  <div>
                    Popcorn spices | <PopcornSeasoning text="Buy" />
                  </div>
                </div>
                <div>
                  <Image width={90} height={90} alt="tofu" src={SaltAlt} />
                  <div>Salt</div>
                </div>
                <div>
                  <Image width={90} height={90} alt="tofu" src={SteviaImage} />
                  <div>
                    Stevia | <Stevia text="Buy" />
                  </div>
                </div>
                <div>
                  <Image
                    width={90}
                    height={90}
                    alt="sucralose"
                    src={SucraloseImage}
                  />
                  <div>
                    Sucralose | <Sucralose text="Buy" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default Groceries
